/* Leaflet-powered store directory. All search and distance calculations stay in the browser. */
(() => {
  'use strict';
  let leafletReady;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const coordinate = value => value === '' || value == null ? NaN : Number(value);
  function distance(a, b) {
    const rad = Math.PI / 180, lat = (b.lat - a.lat) * rad, lng = (b.lng - a.lng) * rad;
    const h = Math.sin(lat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(lng / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(Math.max(0, 1 - h)));
  }
  function loadAsset(tag, url) {
    return new Promise((resolve, reject) => {
      const el = document.createElement(tag);
      const timer = setTimeout(() => fail(), 15000);
      function fail() { clearTimeout(timer); el.remove(); reject(new Error('Map asset unavailable')); }
      el.onload = () => { clearTimeout(timer); resolve(); };
      el.onerror = fail;
      if (tag === 'link') { el.rel = 'stylesheet'; el.href = url; }
      else { el.src = url; el.async = true; }
      document.head.append(el);
    });
  }
  function loadLeaflet(element) {
    if (!leafletReady) leafletReady = Promise.all([
      loadAsset('link', element.dataset.leafletCss),
      window.L ? Promise.resolve() : loadAsset('script', element.dataset.leafletJs)
    ]).then(() => Promise.all([
      loadAsset('link', element.dataset.clusterCss),
      window.L.markerClusterGroup ? Promise.resolve() : loadAsset('script', element.dataset.clusterJs)
    ])).catch(error => { leafletReady = null; throw error; });
    return leafletReady;
  }

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.abort = new AbortController();
      this.records = [];
      this.markers = new Map();
      this.geo = null;
      this.limit = 30;
      this.selected = null;
      this.$ = selector => this.querySelector(selector);
      this.list = this.$('[data-store-list]');
      this.search = this.$('[data-store-search]');
      this.state = this.$('[data-store-state]');
      this.retailer = this.$('[data-store-retailer]');
      this.product = this.$('[data-store-product]');
      this.mobile = matchMedia('(max-width: 699.98px)');
      this.dataset.view = 'list';
      this.readCards(this.list.querySelectorAll('[data-store-card]'));
      this.nextURL = this.list.dataset.nextUrl;
      const on = (el, event, callback) => el.addEventListener(event, callback, { signal: this.abort.signal });
      on(this.$('form'), 'submit', event => { event.preventDefault(); this.apply(); });
      on(this.search, 'input', () => { clearTimeout(this.debounce); this.debounce = setTimeout(() => this.apply(), 120); });
      [this.state, this.retailer, this.product].forEach(el => on(el, 'change', () => this.apply()));
      on(this, 'click', event => {
        const select = event.target.closest('[data-store-select]');
        if (select) this.select(select.closest('[data-store-card]').dataset.storeId, false);
        if (event.target.closest('[data-store-reset], [data-empty-reset]')) this.reset();
        if (event.target.closest('[data-store-more]')) { this.limit += 30; this.render(false); }
        if (event.target.closest('[data-store-locate]')) this.locate();
        if (event.target.closest('[data-map-reset]')) this.fitMap();
        if (event.target.closest('[data-map-retry]')) this.retryMap();
        if (event.target.closest('[data-store-retry]')) this.loadRemaining();
        const view = event.target.closest('[data-store-view]');
        if (view) this.setView(view.dataset.storeView);
      });
      this.resize = () => { if (!this.mobile.matches || this.dataset.view === 'map') this.ensureMap().then(() => this.map?.invalidateSize()); };
      on(this.mobile, 'change', this.resize);
      this.querySelectorAll('[data-locator-controls]').forEach(el => { el.hidden = false; });
      this.refreshOptions();
      this.apply();
      this.loadRemaining();
      this.mapObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && (!this.mobile.matches || this.dataset.view === 'map')) this.ensureMap();
      }, { rootMargin: '100px' });
      this.mapObserver.observe(this.$('.cu-locator__map-wrap'));
      this.mapResize = new ResizeObserver(() => this.map?.invalidateSize());
      this.mapResize.observe(this.$('.cu-locator__map-wrap'));
    }

    readCards(cards) {
      for (const card of cards) {
        if (this.records.some(record => record.id === card.dataset.storeId)) continue;
        const data = card.dataset, lat = coordinate(data.lat), lng = coordinate(data.lng);
        this.records.push({ id: data.storeId, card, name: data.name, state: data.state, retailer: data.retailer,
          products: (data.products || '').split(',').filter(Boolean),
          text: normalize([data.name, data.retailer, data.city, data.state, data.postcode, data.address].join(' ')),
          lat, lng, mapped: Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 });
      }
    }

    async loadRemaining() {
      if (this.loading) return;
      if (this.partial) this.notice('');
      this.loading = true;
      this.$('[data-store-retry]').hidden = true;
      const seen = new Set();
      try {
        while (this.nextURL) {
          if (seen.has(this.nextURL)) throw new Error('Repeated store page');
          seen.add(this.nextURL);
          const url = new URL(this.nextURL, location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('view', 'clearup-stores');
          const response = await fetch(url, { signal: this.abort.signal });
          if (!response.ok) throw new Error('Store page unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const list = document.querySelector('[data-store-list]');
          if (!list) throw new Error('Store list missing');
          const cards = [...list.querySelectorAll('[data-store-card]')];
          this.readCards(cards);
          this.nextURL = list.dataset.nextUrl;
          this.refreshOptions();
          this.apply();
        }
        this.partial = false;
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.partial = true;
        this.notice('Some stores couldn’t load. You can retry or browse the stores already listed.');
        this.$('[data-store-retry]').hidden = false;
      } finally {
        this.loading = false;
        if (this.isConnected) this.render(false);
      }
    }

    refreshOptions() {
      const fill = (select, values) => {
        const previous = select.value;
        select.querySelectorAll('option:not(:first-child)').forEach(option => option.remove());
        values.forEach(([value, label]) => select.add(new Option(label, value)));
        select.value = previous;
        if (select.selectedIndex < 0) select.value = '';
      };
      const unique = field => [...new Set(this.records.map(record => record[field]).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map(value => [value, value]);
      fill(this.state, unique('state'));
      fill(this.retailer, unique('retailer'));
      this.$('[data-retailer-filter]').hidden = this.retailer.options.length <= 2;
      const products = new Map();
      this.records.forEach(record => record.card.querySelectorAll('[data-product-id]').forEach(el => products.set(el.dataset.productId, el.textContent)));
      fill(this.product, [...products].sort((a, b) => a[1].localeCompare(b[1])));
      this.$('[data-product-filter]').hidden = products.size === 0;
      this.state.disabled = this.records.length === 0;
    }

    apply() {
      this.limit = 30;
      this.selected = null;
      this.map?.closePopup();
      const words = normalize(this.search.value).split(' ').filter(Boolean);
      this.filtered = this.records.filter(record => words.every(word => record.text.includes(word)) &&
        (!this.state.value || record.state === this.state.value) && (!this.retailer.value || record.retailer === this.retailer.value) &&
        (!this.product.value || record.products.includes(this.product.value)));
      this.records.forEach(record => { record.distance = this.geo && record.mapped ? distance(this.geo, record) : Infinity; });
      this.filtered.sort((a, b) => (this.geo ? a.distance - b.distance : 0) || a.name.localeCompare(b.name));
      this.render(true);
    }

    render(updateMap) {
      if (!this.filtered) return;
      const filteredSet = new Set(this.filtered);
      const fragment = document.createDocumentFragment();
      this.filtered.forEach((record, index) => {
        record.card.hidden = index >= this.limit;
        record.card.classList.toggle('is-selected', record.id === this.selected);
        const label = record.card.querySelector('[data-store-distance]');
        label.hidden = !Number.isFinite(record.distance);
        if (!label.hidden) label.textContent = (record.distance < 10 ? record.distance.toFixed(1) : Math.round(record.distance)) + ' km away';
        fragment.append(record.card);
      });
      this.records.filter(record => !filteredSet.has(record)).forEach(record => { record.card.hidden = true; fragment.append(record.card); });
      this.list.append(fragment);
      const count = this.filtered.length;
      this.$('[data-store-count]').textContent = this.records.length ? count + (count === 1 ? ' store' : ' stores') + (this.geo ? ' · nearest first' : ' in Malaysia') + (this.loading ? ' · loading directory' : this.partial ? ' · partial directory' : '') : 'Find your local stockist';
      this.$('[data-store-more]').hidden = count <= this.limit;
      this.$('[data-store-reset]').hidden = !(this.search.value || this.state.value || this.retailer.value || this.product.value || this.geo);
      this.$('[data-store-empty]').hidden = count > 0;
      this.$('[data-empty-title]').textContent = this.records.length ? 'No stores found' : 'Find ClearUp at Watsons';
      this.$('[data-empty-copy]').textContent = this.records.length ? 'Try another city or postcode, or clear your filters to see more stores.' : 'Our branch directory is on its way. In the meantime, ask us for help finding ClearUp near you.';
      this.$('[data-empty-help]').hidden = this.records.length > 0;
      this.$('[data-empty-reset]').hidden = this.records.length === 0;
      if (updateMap && this.map) { this.syncMarkers(); this.fitMap(); }
    }

    reset() {
      this.locationRequest = (this.locationRequest || 0) + 1;
      this.$('[data-store-locate]').disabled = false;
      this.$('[data-store-locate]').removeAttribute('aria-busy');
      this.search.value = ''; this.state.value = ''; this.retailer.value = ''; this.product.value = '';
      this.geo = null;
      if (this.userMarker) { this.userMarker.remove(); this.userMarker = null; }
      this.notice(''); this.apply();
    }
    notice(message) { const el = this.$('[data-store-notice]'); el.textContent = message; el.hidden = !message; }

    locate() {
      const button = this.$('[data-store-locate]');
      const request = this.locationRequest = (this.locationRequest || 0) + 1;
      if (!navigator.geolocation) { this.notice('Location isn’t available in this browser. Search by city or postcode instead.'); return; }
      button.disabled = true; button.setAttribute('aria-busy', 'true');
      this.notice('Finding your location…');
      navigator.geolocation.getCurrentPosition(position => {
        if (!this.isConnected || request !== this.locationRequest) return;
        button.disabled = false; button.removeAttribute('aria-busy');
        this.geo = { lat: position.coords.latitude, lng: position.coords.longitude };
        this.notice(this.records.length ? 'Showing nearest matches. Distances are straight-line estimates.' : 'Your location is ready. Branches will appear once the directory is available.');
        this.apply();
        if (this.map) this.showLocation();
      }, error => {
        if (!this.isConnected || request !== this.locationRequest) return;
        button.disabled = false; button.removeAttribute('aria-busy');
        this.notice(error.code === 1 ? 'Location permission wasn’t granted. Search by city or postcode instead.' : 'We couldn’t find your location. Search by city or postcode instead.');
      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    }

    setView(view) {
      this.dataset.view = view;
      this.querySelectorAll('[data-store-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.storeView === view)));
      if (view === 'map') this.ensureMap().then(() => { this.map?.invalidateSize(); if (this.map && this.geo) this.showLocation(); else this.fitMap(); });
    }

    ensureMap() {
      if (this.map) return Promise.resolve();
      if (!this.mapTask) this.mapTask = this.createMap().finally(() => { this.mapTask = null; });
      return this.mapTask;
    }
    async createMap() {
      if (!this.isConnected) return;
      this.mapLoading = true;
      try {
        await loadLeaflet(this);
        if (!this.isConnected) return;
        this.$('[data-map-loading]').hidden = true;
        const L = window.L;
        this.map = L.map(this.$('[data-store-map]'), { scrollWheelZoom: false, zoomControl: true, maxZoom: 18, minZoom: 3 });
        this.map.attributionControl.setPrefix('<a href="https://leafletjs.com" target="_blank" rel="noopener">Leaflet</a>');
        const tileURL = this.dataset.tileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
        if (!tileURL.startsWith('https://')) throw new Error('Map provider must use HTTPS');
        const provider = document.createElement('span'); provider.textContent = this.dataset.tileAttribution || '';
        this.tiles = L.tileLayer(tileURL, { maxZoom: 18, keepBuffer: 1, updateWhenIdle: true, noWrap: true, referrerPolicy: 'strict-origin-when-cross-origin', attribution: (provider.innerHTML ? provider.innerHTML + ' · ' : '') + '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors' });
        this.tileErrors = 0;
        this.tiles.on('tileerror', () => { if (++this.tileErrors >= 3) this.mapError(); });
        this.tiles.on('tileload', () => { this.tileErrors = 0; this.$('[data-map-message]').hidden = true; });
        this.tiles.addTo(this.map);
        this.layers = L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 44, animate: false,
          iconCreateFunction: cluster => L.divIcon({ className: 'cu-locator__cluster', html: String(cluster.getChildCount()), iconSize: [40, 40] }) }).addTo(this.map);
        this.syncMarkers(); this.fitMap();
        this.$('[data-map-reset]').hidden = false;
        if (this.geo) this.showLocation();
      } catch (error) { this.map?.remove(); this.map = null; this.layers = null; this.mapError(); }
      finally { this.mapLoading = false; }
    }

    syncMarkers() {
      this.layers.clearLayers(); this.markers.clear();
      this.filtered.filter(record => record.mapped).forEach((record, index) => {
        const icon = L.divIcon({ className: 'cu-locator__marker', html: String(index + 1), iconSize: [28, 28], iconAnchor: [14, 14] });
        const marker = L.marker([record.lat, record.lng], { icon, title: record.name, alt: record.name, keyboard: true });
        const popup = document.createElement('div'); popup.className = 'cu-locator__popup';
        const title = document.createElement('h3'); title.textContent = record.name;
        const address = document.createElement('p'); address.textContent = record.card.dataset.address;
        const directions = record.card.querySelector('[data-store-directions]').cloneNode(true);
        popup.append(title, address, directions);
        marker.bindPopup(popup, { maxWidth: 260 }).on('click', () => this.select(record.id, true));
        marker.addTo(this.layers); this.markers.set(record.id, marker);
      });
    }
    fitMap() {
      if (!this.map || !this.layers) return;
      const coordinates = this.filtered.filter(record => record.mapped).map(record => [record.lat, record.lng]);
      if (coordinates.length) this.map.fitBounds(coordinates, { padding: [38, 38], maxZoom: 13, animate: false });
      else this.map.setView([4.4, 109.2], 5, { animate: false });
    }
    showLocation() {
      if (this.userMarker) this.userMarker.remove();
      this.userMarker = L.circleMarker([this.geo.lat, this.geo.lng], { radius: 7, color: '#fff', weight: 3, fillColor: '#3478c8', fillOpacity: 1 }).bindTooltip('Your location').addTo(this.map);
      const nearest = this.filtered.find(record => record.mapped);
      if (nearest) this.map.fitBounds([[this.geo.lat, this.geo.lng], [nearest.lat, nearest.lng]], { padding: [40, 40], maxZoom: 13, animate: false });
      else this.map.setView([this.geo.lat, this.geo.lng], 11, { animate: false });
    }
    select(id, fromMap) {
      const record = this.filtered.find(item => item.id === id);
      if (!record) return;
      this.selected = id;
      this.limit = Math.max(this.limit, this.filtered.indexOf(record) + 1);
      this.render(false);
      this.markers.forEach((marker, key) => marker.getElement()?.classList.toggle('is-selected', key === id));
      if (fromMap) {
        const results = this.$('.cu-locator__results');
        results.scrollTop += record.card.getBoundingClientRect().top - results.getBoundingClientRect().top;
      } else if (record.mapped) {
        if (this.mobile.matches) this.setView('map');
        this.ensureMap().then(() => {
          const marker = this.markers.get(id);
          if (marker) this.layers.zoomToShowLayer(marker, () => {
            marker.getElement()?.classList.add('is-selected');
            marker.openPopup();
          });
        });
      } else this.notice('This branch’s map pin isn’t available yet. Use Get directions to find its address.');
    }
    mapError() { this.$('[data-map-loading]').hidden = true; this.$('[data-map-message]').hidden = false; }
    retryMap() {
      this.$('[data-map-message]').hidden = true;
      if (this.map && this.tiles) { this.tileErrors = 0; this.tiles.redraw(); }
      else { this.map?.remove(); this.map = null; this.ensureMap(); }
    }
    disconnectedCallback() {
      this.abort?.abort(); clearTimeout(this.debounce); this.mapObserver?.disconnect(); this.mapResize?.disconnect(); this.map?.remove();
      this.map = null; this.started = false;
    }
  }
  if (!customElements.get('cu-store-locator')) customElements.define('cu-store-locator', StoreLocator);
})();
